import { useState, useEffect, useRef } from 'react';
import { supabase, type NewsItem } from '@/lib/supabase';
import { slugify } from '@/lib/slugify';

interface Props {
  editItem?: NewsItem | null;
  onClose: () => void;
  onSaved: () => void;
}

const CATEGORIES = [
  { id: 'yapay-zeka', name: 'Yapay Zeka' },
  { id: 'mobil', name: 'Mobil' },
  { id: 'oyun', name: 'Oyun' },
  { id: 'donanim', name: 'Donanım' },
  { id: 'yazilim', name: 'Yazılım' },
  { id: 'girisimcilik', name: 'Girişimcilik' },
  { id: 'kripto', name: 'Kripto' },
  { id: 'bilim', name: 'Bilim' },
  { id: 'siber-guvenlik', name: 'Siber Güvenlik' },
  { id: 'otomotiv', name: 'Otomotiv' },
  { id: 'saglik', name: 'Sağlık' },
  { id: 'uzay', name: 'Uzay' },
  { id: 'fintech', name: 'Fintech' },
  { id: 'spor-teknolojisi', name: 'Spor Teknolojisi' },
  { id: 'egitim-teknolojisi', name: 'Eğitim Teknolojisi' },
  { id: 'cevre-enerji', name: 'Çevre & Enerji' },
  { id: 'drone-robot', name: 'Drone & Robot' },
  { id: 'metaverse-arvr', name: 'Metaverse & AR/VR' },
];

const empty = {
  title: '', excerpt: '', content: '', category: 'yapay-zeka',
  author: '', date: new Date().toISOString().slice(0, 10),
  read_time: '5 dk', views: 0, image: '', featured: false,
};

type ImageTab = 'upload' | 'url';

export default function NewsForm({ editItem, onClose, onSaved }: Props) {
  const [form, setForm] = useState({ ...empty });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imageTab, setImageTab] = useState<ImageTab>('upload');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editItem) {
      setForm({
        title: editItem.title,
        excerpt: editItem.excerpt,
        content: editItem.content,
        category: editItem.category,
        author: editItem.author,
        date: editItem.date,
        read_time: editItem.read_time,
        views: editItem.views,
        image: editItem.image,
        featured: editItem.featured,
      });
    }
  }, [editItem]);

  const set = (key: string, val: string | boolean | number) =>
    setForm(f => ({ ...f, [key]: val }));

  const handleFileUpload = async (file: File) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Dosya boyutu 5MB\'dan büyük olamaz.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Sadece resim dosyaları yüklenebilir.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');

    const ext = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

    // Simulate progress
    const progressInterval = setInterval(() => {
      setUploadProgress(p => Math.min(p + 15, 85));
    }, 150);

    const { data, error: uploadError } = await supabase.storage
      .from('news-images')
      .upload(fileName, file, { cacheControl: '3600', upsert: false });

    clearInterval(progressInterval);

    if (uploadError) {
      setError('Resim yüklenemedi: ' + uploadError.message);
      setUploading(false);
      setUploadProgress(0);
      return;
    }

    setUploadProgress(100);

    const { data: urlData } = supabase.storage
      .from('news-images')
      .getPublicUrl(data.path);

    set('image', urlData.publicUrl);
    setUploading(false);
    setTimeout(() => setUploadProgress(0), 500);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleRemoveImage = () => set('image', '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.excerpt.trim() || !form.content.trim() || !form.author.trim()) {
      setError('Lütfen zorunlu alanları doldurun.');
      return;
    }
    setLoading(true);
    setError('');
    const slug = slugify(form.title);
    if (editItem) {
      const { error: err } = await supabase.from('news').update({ ...form, slug }).eq('id', editItem.id);
      if (err) { setError('Güncelleme başarısız.'); setLoading(false); return; }
    } else {
      const { error: err } = await supabase.from('news').insert([{ ...form, slug }]);
      if (err) { setError('Kaydetme başarısız.'); setLoading(false); return; }
    }
    setLoading(false);
    onSaved();
  };

  const inputCls = 'w-full bg-white border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-[#0A0E27] focus:outline-none focus:border-[#0A0E27]';
  const labelCls = 'block text-xs font-semibold text-gray-500 mb-1';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="font-bold text-[#0A0E27] text-lg">
            {editItem ? 'Haberi Düzenle' : 'Yeni Haber Ekle'}
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-4">
          {/* Title */}
          <div>
            <label className={labelCls}>Başlık <span className="text-red-400">*</span></label>
            <input type="text" value={form.title} onChange={e => set('title', e.target.value)} placeholder="Haber başlığı" className={inputCls} />
          </div>

          {/* Category & Author */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Kategori</label>
              <select value={form.category} onChange={e => set('category', e.target.value)} className={inputCls}>
                {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Yazar <span className="text-red-400">*</span></label>
              <input type="text" value={form.author} onChange={e => set('author', e.target.value)} placeholder="Yazar adı" className={inputCls} />
            </div>
          </div>

          {/* Date & Read Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Tarih</label>
              <input type="date" value={form.date} onChange={e => set('date', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Okuma Süresi</label>
              <input type="text" value={form.read_time} onChange={e => set('read_time', e.target.value)} placeholder="5 dk" className={inputCls} />
            </div>
          </div>

          {/* Image Section */}
          <div>
            <label className={labelCls}>Kapak Görseli</label>

            {/* Tab Switcher */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 mb-3 w-fit">
              <button
                type="button"
                onClick={() => setImageTab('upload')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${imageTab === 'upload' ? 'bg-white text-[#0A0E27]' : 'text-gray-500 hover:text-[#0A0E27]'}`}
              >
                <i className="ri-upload-cloud-line" />Dosya Yükle
              </button>
              <button
                type="button"
                onClick={() => setImageTab('url')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${imageTab === 'url' ? 'bg-white text-[#0A0E27]' : 'text-gray-500 hover:text-[#0A0E27]'}`}
              >
                <i className="ri-link" />URL Gir
              </button>
            </div>

            {/* Upload Tab */}
            {imageTab === 'upload' && (
              <div>
                {!form.image ? (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                      dragOver
                        ? 'border-[#0A0E27] bg-[#0A0E27]/5'
                        : 'border-gray-200 hover:border-[#0A0E27]/40 hover:bg-gray-50'
                    }`}
                  >
                    {uploading ? (
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#0A0E27]/10">
                          <i className="ri-loader-4-line text-[#0A0E27] text-xl animate-spin" />
                        </div>
                        <p className="text-sm font-semibold text-[#0A0E27]">Yükleniyor...</p>
                        <div className="w-full max-w-xs bg-gray-200 rounded-full h-1.5">
                          <div
                            className="bg-[#0A0E27] h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-400">{uploadProgress}%</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100">
                          <i className="ri-image-add-line text-gray-400 text-xl" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-[#0A0E27]">
                            {dragOver ? 'Bırakın!' : 'Resim yüklemek için tıklayın'}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">veya sürükleyip bırakın</p>
                          <p className="text-xs text-gray-300 mt-1">PNG, JPG, WEBP — Maks. 5MB</p>
                        </div>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                ) : (
                  <div className="relative rounded-xl overflow-hidden">
                    <img src={form.image} alt="preview" className="w-full h-48 object-cover object-top" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center gap-1.5 bg-white text-[#0A0E27] text-xs font-bold px-3 py-2 rounded-lg cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-refresh-line" />Değiştir
                      </button>
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="flex items-center gap-1.5 bg-red-500 text-white text-xs font-bold px-3 py-2 rounded-lg cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-delete-bin-line" />Kaldır
                      </button>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                )}
              </div>
            )}

            {/* URL Tab */}
            {imageTab === 'url' && (
              <div>
                <input
                  type="text"
                  value={form.image}
                  onChange={e => set('image', e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className={inputCls}
                />
                {form.image && (
                  <div className="mt-2 relative rounded-xl overflow-hidden">
                    <img
                      src={form.image}
                      alt="preview"
                      className="w-full h-40 object-cover object-top"
                      onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-red-500 text-white rounded-full cursor-pointer hover:bg-red-600 transition-colors"
                    >
                      <i className="ri-close-line text-sm" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Excerpt */}
          <div>
            <label className={labelCls}>Özet <span className="text-red-400">*</span></label>
            <textarea
              value={form.excerpt}
              onChange={e => set('excerpt', e.target.value.slice(0, 300))}
              rows={2}
              maxLength={300}
              placeholder="Kısa özet..."
              className={`${inputCls} resize-none`}
            />
            <div className="text-xs text-gray-400 text-right">{form.excerpt.length}/300</div>
          </div>

          {/* Content */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={labelCls + ' mb-0'}>İçerik <span className="text-red-400">*</span></label>
              <div className="flex items-center gap-2">
                {/* Auto-link URLs button */}
                <button
                  type="button"
                  onClick={() => {
                    const linked = form.content.replace(
                      /(?<!href=["']|src=["']|["'>])(https?:\/\/[^\s<>"')\]]+)/g,
                      (url) => `<a href="${url}" target="_blank" rel="nofollow noopener noreferrer">${url}</a>`
                    );
                    if (linked === form.content) {
                      setError('İçerikte dönüştürülecek ham URL bulunamadı.');
                      setTimeout(() => setError(''), 2500);
                    } else {
                      set('content', linked);
                    }
                  }}
                  title="İçerikteki ham URL\'leri tıklanabilir bağlantıya çevirir"
                  className="flex items-center gap-1.5 bg-[#0A0E27]/5 hover:bg-[#0A0E27]/10 text-[#0A0E27] border border-[#0A0E27]/15 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-links-line" />URL&apos;leri Linkleştir
                </button>
                {/* HTML tag formatter */}
                <button
                  type="button"
                  onClick={() => {
                    const formatted = form.content
                      .replace(/\n{2,}/g, '</p><p>')
                      .replace(/^(.+)$/, '<p>$1</p>');
                    set('content', formatted);
                  }}
                  title="Paragrafları <p> etiketine çevirir"
                  className="flex items-center gap-1.5 bg-gray-50 hover:bg-gray-100 text-gray-500 border border-gray-200 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-code-s-slash-line" />&lt;p&gt; Dönüştür
                </button>
              </div>
            </div>
            <textarea
              value={form.content}
              onChange={e => set('content', e.target.value)}
              rows={8}
              placeholder="Haber içeriği (HTML desteklenir)..."
              className={`${inputCls} resize-none font-mono text-xs leading-relaxed`}
            />
            <p className="text-[10px] text-gray-400 mt-1">
              <i className="ri-information-line mr-1" />
              HTML desteklenir: &lt;h2&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;a href=&quot;...&quot;&gt;, &lt;ul&gt;&lt;li&gt;, &lt;blockquote&gt;
            </p>
          </div>

          {/* Featured */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="featured"
              checked={form.featured}
              onChange={e => set('featured', e.target.checked)}
              className="w-4 h-4 cursor-pointer"
            />
            <label htmlFor="featured" className="text-sm text-gray-600 cursor-pointer">
              Öne çıkan haber olarak işaretle
            </label>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-500 bg-red-50 rounded-lg px-4 py-2.5">
              <i className="ri-error-warning-line" />{error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-200 text-gray-600 rounded-lg py-2.5 text-sm font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={loading || uploading}
              className="flex-1 bg-[#0A0E27] text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-[#0A0E27]/80 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {loading ? 'Kaydediliyor...' : editItem ? 'Güncelle' : 'Yayınla'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
